import { animate, transition, trigger } from '@angular/animations';
import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProfileFacade } from '@fantaz/profile/data-access';
import {
  ApplyPromoCodeFacade,
  PromoCodeFacade,
} from '@fantaz/promo-code/data-access';
import { environment } from '@fantaz/shared/app-config';
import * as fromAuth from '@fantaz/shared/auth';
import { loadUser } from '@fantaz/shared/auth';
import { FZ_ZENDESK_URLS } from '@fantaz/shared/config';
import {
  PushNotificationService,
  TournamentApiService,
} from '@fantaz/shared/data-access/api';
import {
  CurrentUser,
  Profile,
  WalletModel,
  WebsocketMessage,
} from '@fantaz/shared/data-access/models';
import {
  ColorScheme,
  ColorSchemeService,
} from '@fantaz/shared/services/color-scheme';
import { ShareService } from '@fantaz/shared/share';
import { RouterUtil } from '@fantaz/shared/utils/router';
import { ToastService } from '@fantaz/shared/utils/toast';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { Observable, Subject, fromEvent, take, takeUntil } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  share,
  throttleTime,
} from 'rxjs/operators';

enum StickState {
  Sticked = 'sticked',
  None = 'none',
}

enum Direction {
  Up = 'Up',
  Down = 'Down',
}

@UntilDestroy()
@Component({
  selector: 'fz-navbar',
  styleUrls: ['./navbar.component.scss'],
  templateUrl: './navbar.component.html',
  animations: [
    trigger('toggle', [transition('* => *', animate('200ms ease-in'))]),
  ],
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  #authFacade: fromAuth.AuthFacade = inject(fromAuth.AuthFacade);
  #colorSchemeService = inject(ColorSchemeService);
  // component only impl
  @Input()
  sticky: number;

  @Input()
  variant: 'stick' | 'permanent' | 'default' = 'stick';

  @Input()
  theme?: string = 'dark';

  @Input()
  enableSwitchTheme?: boolean = false;

  isSticked = false;
  profile: Profile | null;

  @HostBinding('@toggle')
  get toggle(): StickState {
    return this.isSticked ? StickState.Sticked : StickState.None;
  }

  // business impl
  user$: Observable<CurrentUser | null | undefined> =
    new Observable<CurrentUser | null>();
  wallet$: Observable<WalletModel | null | undefined> =
    new Observable<WalletModel | null>();
  isLoggedIn$: Observable<boolean> = new Observable();
  locationError$: Observable<string> = new Observable();
  profile$ = this.profileFacade.profile$;

  unsubscribe$: Subject<void> = new Subject<void>();
  isLoggedIn: boolean;
  locationError: string;
  invite: string | undefined;
  membershipType: string | undefined;
  currentUser: CurrentUser | null | undefined;
  window: any = window;
  playPage = RouterUtil.Configuration.Games;

  constructor(
    private store: Store,
    private shareService: ShareService,
    private toastService: ToastService,
    private location: Location,
    private route: ActivatedRoute,
    public readonly router: Router,
    private tournamentApiService: TournamentApiService,
    private promoCodeFacade: PromoCodeFacade,
    private readonly promoCodeFacade2: ApplyPromoCodeFacade,
    private readonly profileFacade: ProfileFacade,
    private pushNotificationService: PushNotificationService,
  ) {}

  ngOnInit(): void {
    this.tournamentApiService.unsupportedBrowserValidation();
    // TODO: Move this condition to specific app initialization
    if (this.sticky) {
      // initial state
      if (window.pageYOffset < this.sticky) {
        this.isSticked = true;
      }
      this.promoCodeFacade.loadFromLocalStorage();
    }

    this.checkNotFoundError();
    this.getLoggedIn();
    this.getUser();
    this.getWallet();
    this.getSponsorInvitation();
    this.getActiveFragment();
    this.checkTransactionNotification();
    this.updatePlayPageUrl();
  }

  ngAfterViewInit() {
    if (this.sticky) {
      const scroll$ = fromEvent(window, 'scroll').pipe(
        throttleTime(10),
        map(() => window.pageYOffset),
        pairwise(),
        map(
          ([y1, y2]): Direction =>
            y2 < this.sticky ? Direction.Up : Direction.Down,
        ),
        distinctUntilChanged(),
        share(),
      );

      const goingUp$ = scroll$.pipe(
        filter((direction) => direction === Direction.Up),
      );

      const goingDown$ = scroll$.pipe(
        filter((direction) => direction === Direction.Down),
      );

      goingUp$.subscribe(() => (this.isSticked = true));
      goingDown$.subscribe(() => (this.isSticked = false));
    }
  }

  get playActive() {
    return (
      this.router.url.includes(this.playPage) ||
      this.window.location.pathname.includes(
        RouterUtil.Configuration.Tournaments,
      )
    );
  }

  checkTransactionNotification(): void {
    const queryParams = this.route.snapshot.root.queryParams;
    const path = this.location.path();
    if (queryParams.notifyTransaction && path.indexOf('wallet') === -1) {
      this.toastService.openSnackBar(
        `Your wallet is in processing. it'll take a few minutes`,
      );
    }
  }

  checkNotFoundError() {
    const params = this.route.snapshot.root.queryParams;

    if (params['errorCode'] != null) {
      console.error('Error', params);
      this.toastService.openSnackBar(params['errorMessage'], {
        duration: 10000,
      });
    }
  }

  getActiveFragment() {
    this.route.fragment.pipe(take(1)).subscribe((fragment) => {
      switch (fragment) {
        case 'promote':
          this.onPromote(true);
          break;
        case 'promo-code':
          this.onPromoCode();
          break;

        default:
          break;
      }
    });
  }

  getSponsorInvitation() {
    // TODO: move for entry point
    const routeParams = this.router.routerState.snapshot.root.queryParams;
    let invite = routeParams.inviteBy;
    if (invite) {
      localStorage.setItem('sponsorName', invite);
    } else {
      invite = localStorage.getItem('sponsorName');
    }
    // !FIXME: this.authService.setInvite(invite);
  }

  updatePlayPageUrl() {
    this.profile$.pipe(takeUntil(this.unsubscribe$)).subscribe((profile) => {
      this.profile = profile || null;
    });
  }

  getUser() {
    this.playPage = localStorage.getItem('playPage') || this.playPage;
    this.user$ = this.store.select(fromAuth.getCurrentUser).pipe(
      filter((u) => u !== null),
      untilDestroyed(this),
    );
    this.user$.subscribe((currentUser) => {
      this.applyUserThemeSettings(currentUser);
      this.currentUser = currentUser;
      const playPage =
        currentUser?.user?.userSettings?.playPage ?? this.playPage;
      this.playPage = playPage;
    });
  }

  private applyUserThemeSettings(currentUser: CurrentUser | null | undefined) {
    if (!this.currentUser && currentUser?.user?.userSettings?.theme_scheme) {
      const theme: ColorScheme = currentUser?.user?.userSettings
        ?.theme_scheme as ColorScheme;
      const currentScheme = this.#colorSchemeService.get();
      if (currentScheme !== theme) {
        this.#colorSchemeService.set(theme);
      }
    }
  }

  getWallet() {
    this.wallet$ = this.store
      .select(fromAuth.getWallet)
      .pipe(untilDestroyed(this));
  }

  getLoggedIn() {
    // TODO: remove auth initialization from here, move to /apps/web and usage AuthFacade instanceof pure store call
    this.isLoggedIn$ = this.#authFacade.isLoggedIn$.pipe(
      takeUntil(this.unsubscribe$),
    );
    this.isLoggedIn$.subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn;
      if (isLoggedIn) {
        this.invite = undefined;
        const path = this.getLocationPath();
        this.pushNotificationService.init(
          (message: WebsocketMessage) => {
            console.log('Starting websocket notification');
            if (message && message.body && message.body.transactionId) {
              this.store.dispatch(loadUser());
            }
          },
          ['PLAY_INSTANCE_FINALIZED'],
        );
      }
    });
  }

  getLocationPath(): string {
    const url = new URL(window.location.href);
    return url.pathname;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.pushNotificationService.closeSocket();
  }

  onLogout() {
    this.#authFacade.logout();
  }

  onLogin() {
    window.location.href = `/auth/sign-in?redirectTo=${window.location.pathname}`;
  }

  onSignUp() {
    window.location.href = `/auth/sign-up?redirectTo=${window.location.pathname}`;
  }

  onPromote(hasFragment?: boolean) {
    let profileLink: string = environment.baseURL + '/join';
    const params = {
      title: 'FantaZ',
      text: 'Play Casual tournaments',
      type: 'promote',
      url: environment.baseURL + '/join',
      currentUser: this.currentUser,
    };
    this.updateFragment(hasFragment, 'promote');
    this.shareService.share(params);
  }

  private updateFragment(hasFragment?: boolean, fragment?: string) {
    if (!hasFragment && fragment) {
      const pathWithoutHash = this.location.path();
      this.router.navigateByUrl(`${pathWithoutHash}#${fragment}`);
      this.router.onSameUrlNavigation = 'reload';
    }
  }

  openErrorDialog() {
    this.toastService
      .openSnackBar(
        'For compliance purposes, we would like to validate your location.',
        {
          action: 'MORE DETAILS',
          duration: 10000,
        },
      )
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        this.openUrl(FZ_ZENDESK_URLS.location);
      });
  }

  onPromoCode() {
    this.promoCodeFacade2.openPromoCodeDialog();
  }

  onThemeChange(theme: string) {
    if (this.profile) {
      const settings = this.profile.userSettings
        ? cloneDeep(this.profile.userSettings)
        : {};
      if (this.profile.userSettings?.theme_scheme !== theme) {
        settings.theme_scheme = theme;
        const payload: Profile = {
          ...this.profile,
          userSettings: settings,
        };
        this.profileFacade.updateProfileSettings(payload, {
          silent: true,
        });
      }
    }
  }

  private openUrl(url: string) {
    try {
      this.window.open(url, '_blank').focus();
    } catch (error) {
      console.log('Error with share link: ' + url, error);
    }
  }
}
